import type { PerpsBalance } from '@infinex/types';

import { isUsdcSpotToken } from './selectWithdrawableUsdc';

/**
 * USD value double-reported by the two ledgers of a unified Hyperliquid
 * account, for one account's balance rows.
 *
 * On a unified account, an open perp position is collateralized by spot
 * USDC placed on hold: Hyperliquid reports the same dollars once inside
 * the spot USDC `total` AND once as the perp clearinghouse `accountValue`
 * (observed live: hold == accountValue to 5 decimals while a position is
 * open). Any total that adds perp value and spot value must subtract this
 * overlap, or opening a position inflates the account by its own margin.
 *
 * `min()` is what makes this safe for every shape:
 * - spot USDC hold can also come from open spot ORDERS, which do not
 *   overlap perp value — capping at the perp value leaves those counted;
 * - the perp-backing hold never exceeds what is actually held — capping at
 *   the hold avoids over-subtracting;
 * - legacy (non-unified) accounts have no spot rows or a zero hold, so the
 *   overlap degrades to 0 and plain addition is preserved.
 *
 * Scope: pass ONE account's rows (e.g. `snapshot.balances`). Mixing
 * accounts would min() across unrelated holds. Only Hyperliquid rows are
 * considered — no other provider has spot-collateralized perps.
 */
export function selectUnifiedMarginOverlap(
  balances: readonly PerpsBalance[]
): number {
  let perpValue = 0;
  let usdcHold = 0;

  for (const balance of balances) {
    if (balance.providerId !== 'hyperliquid') continue;

    if (
      balance.productKind === 'perp' &&
      balance.balanceKind === 'perp_margin'
    ) {
      perpValue += parseAmount(balance.amount);
    } else if (isUsdcSpotToken(balance)) {
      const total = parseAmount(balance.amount);
      const available = Math.max(parseAmount(balance.available), 0);
      usdcHold += Math.max(total - available, 0);
    }
  }

  return Math.max(Math.min(perpValue, usdcHold), 0);
}

export interface SpotNonUsdcAsset {
  asset: string;
  total: number;
  /**
   * USD value (total × markPrice) when the dataset carried a mark price for
   * the token. Absent rows are unpriced — render the token amount instead of
   * silently valuing them at $0.
   */
  usdValue?: number;
}

export interface SpotHoldingsSummary {
  /**
   * True when any spot balance has `total > 0`. Zero-total rows are ignored:
   * Hyperliquid keeps emitting balance rows for tokens an account has fully
   * exited, so a non-empty balances array alone does not indicate funding.
   */
  hasSpotBalance: boolean;
  /**
   * USD value of all priceable spot holdings: USDC valued 1:1 plus non-USDC
   * tokens whose balance row carries a `markPrice` from the dataset's spot
   * market feed. Tokens without a price are reported in
   * {@link nonUsdcAssets} without a `usdValue` instead of being silently
   * valued at $0.
   */
  spotUsdValue: number;
  /** Total spot USDC, including amounts held by open spot orders. */
  spotUsdcTotal: number;
  /** Free spot USDC (total − hold), clamped at 0. */
  spotUsdcAvailable: number;
  /** Spot USDC locked by open spot orders (total − available). */
  spotUsdcHold: number;
  /**
   * Non-USDC spot tokens with a positive balance. These cannot be withdrawn
   * directly (the withdraw flow moves USDC only); `usdValue` is present when
   * the token could be priced.
   */
  nonUsdcAssets: SpotNonUsdcAsset[];
}

const EMPTY_SUMMARY: SpotHoldingsSummary = {
  hasSpotBalance: false,
  spotUsdValue: 0,
  spotUsdcTotal: 0,
  spotUsdcAvailable: 0,
  spotUsdcHold: 0,
  nonUsdcAssets: [],
};

function parseAmount(value: number | string | null | undefined): number {
  const parsed = typeof value === 'number' ? value : Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
}

/**
 * Summarize Hyperliquid-style spot holdings from perps balance rows.
 *
 * Used by the /perps funded gate and value displays so a spot-only unified
 * account (funds land spot-side, perp margin stays 0) renders as funded with
 * coherent values instead of the add-funds empty state (ENG-7059). The
 * USDC predicate mirrors {@link selectWithdrawableUsdc} so the gate, the
 * header value, the account cards and the withdraw dialog all agree on what
 * counts as money.
 */
export function selectSpotHoldings(
  balances: readonly PerpsBalance[]
): SpotHoldingsSummary {
  if (balances.length === 0) return EMPTY_SUMMARY;

  let hasSpotBalance = false;
  let spotUsdValue = 0;
  let spotUsdcTotal = 0;
  let spotUsdcAvailable = 0;
  const nonUsdcAssets: SpotNonUsdcAsset[] = [];

  for (const balance of balances) {
    if (balance.productKind !== 'spot' || balance.balanceKind !== 'spot_token')
      continue;

    const total = parseAmount(balance.amount);
    if (total <= 0) continue;

    hasSpotBalance = true;

    if (isUsdcSpotToken(balance)) {
      spotUsdcTotal += total;
      spotUsdValue += total;
      spotUsdcAvailable += Math.max(parseAmount(balance.available), 0);
    } else {
      const markPrice = parseAmount(balance.markPrice);
      if (markPrice > 0) {
        const usdValue = total * markPrice;
        spotUsdValue += usdValue;
        nonUsdcAssets.push({ asset: balance.asset, total, usdValue });
      } else {
        nonUsdcAssets.push({ asset: balance.asset, total });
      }
    }
  }

  return {
    hasSpotBalance,
    spotUsdValue,
    spotUsdcTotal,
    spotUsdcAvailable,
    spotUsdcHold: Math.max(spotUsdcTotal - spotUsdcAvailable, 0),
    nonUsdcAssets,
  };
}

export interface SpotHoldingDisplayItem {
  /** Provider account (EVM address) holding the balance. */
  accountId: string;
  asset: string;
  total: number;
  /** Free amount (total − held by open spot orders), clamped at 0. */
  available: number;
  /** Amount reserved by open spot orders. */
  hold: number;
  /** USD value when priced: USDC 1:1, others total × markPrice. */
  usdValue?: number;
  /**
   * Unrealized spot PnL (usdValue − entryNotional). Only for priced non-USDC
   * rows with a cost basis — USDC has no meaningful entry.
   */
  pnl?: number;
  /** PnL as a percentage of the entry notional. */
  roePercent?: number;
  isUsdc: boolean;
}

/**
 * Per-token spot holding rows for display (the /perps "Your spot balances"
 * section), mirroring the perps-app's spot balance list: USD value from the
 * dataset mark price, PnL against the Hyperliquid entry notional. Unpriced
 * rows keep `usdValue` undefined so the UI renders the token amount rather
 * than a fabricated $0.
 *
 * Sorted by USD value descending, unpriced rows last (alphabetically).
 */
export function selectSpotHoldingDisplayItems(
  balances: readonly PerpsBalance[]
): SpotHoldingDisplayItem[] {
  const items: SpotHoldingDisplayItem[] = [];

  for (const balance of balances) {
    if (balance.productKind !== 'spot' || balance.balanceKind !== 'spot_token')
      continue;

    const total = parseAmount(balance.amount);
    if (total <= 0) continue;

    const isUsdc = isUsdcSpotToken(balance);
    const markPrice = isUsdc ? 1 : parseAmount(balance.markPrice);
    const usdValue = markPrice > 0 ? total * markPrice : undefined;
    const entryNotional = parseAmount(balance.entryNotional);
    const pnl =
      !isUsdc && usdValue !== undefined && entryNotional > 0
        ? usdValue - entryNotional
        : undefined;

    // Keep available and hold internally consistent: clamp available into
    // [0, total] and derive hold from it, so the "on hold" hint can never
    // exceed the row total even if the provider feed drifts.
    const available = Math.min(
      Math.max(parseAmount(balance.available), 0),
      total
    );
    const hold = Math.max(total - available, 0);

    items.push({
      accountId: balance.accountId,
      asset: balance.asset,
      total,
      available,
      hold,
      ...(usdValue !== undefined ? { usdValue } : {}),
      ...(pnl !== undefined
        ? { pnl, roePercent: (pnl / entryNotional) * 100 }
        : {}),
      isUsdc,
    });
  }

  return items.sort(
    (a, b) =>
      (b.usdValue ?? -1) - (a.usdValue ?? -1) || a.asset.localeCompare(b.asset)
  );
}
